import { useEffect, useState } from 'react';
import { tasksApi, usersApi, companiesApi, dealsApi, leadsApi, contactsApi } from '../api';
import { useAuth } from '../hooks/useAuth';
import SearchableSelect from './SearchableSelect';
import MultiSearchableSelect from './MultiSearchableSelect';

interface Props {
  task?: any;
  companyId?: string;
  contactId?: string;
  leadId?: string;
  dealId?: string;
  defaultDueDate?: string;
  onClose: () => void;
  onSave: () => void;
}

export default function TaskModal({ task, companyId, contactId, leadId, dealId, defaultDueDate, onClose, onSave }: Props) {
  const { user } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [leads, setLeads] = useState<any[]>([]);
  const [deals, setDeals] = useState<any[]>([]);
  const [form, setForm] = useState({
    title: task?.title || '',
    description: task?.description || '',
    status: task?.status || 'PENDING',
    priority: task?.priority || 'MEDIUM',
    dueDate: task?.dueDate ? task.dueDate.split('T')[0] : (defaultDueDate || new Date().toISOString().split('T')[0]),
    dueTime: task?.dueTime || new Date().toTimeString().slice(0, 5),
    assigneeId: task?.assigneeId || '',
    companyId: task?.companyId || companyId || '',
    contactId: task?.contactId || contactId || '',
    leadId: task?.leadId || leadId || '',
    dealId: task?.dealId || dealId || '',
    participantIds: task?.participants?.map((p: any) => p.id) || [],
    participantOrganizationIds: task?.participantOrganizations?.map((o: any) => o.id) || [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!task && user?.id && !form.assigneeId) {
      setForm(prev => ({ ...prev, assigneeId: user.id }));
    }
  }, [user]);

  useEffect(() => {
    const load = async () => {
      try {
        const [usersRes, compRes, contRes, leadsRes, dealsRes] = await Promise.all([
          usersApi.list({ limit: 100 }),
          companiesApi.list({ limit: 100 }),
          contactsApi.list({ limit: 100 }),
          leadsApi.list({ limit: 100 }),
          dealsApi.list({ limit: 100 }),
        ]);
        setUsers(usersRes.data.data || []);
        setCompanies(compRes.data.data || []);
        setContacts(contRes.data.data || []);
        setLeads(leadsRes.data.data || []);
        setDeals(dealsRes.data.data || []);
      } catch {}
    };
    load();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...form,
        dueDate: form.dueDate || null,
        dueTime: form.dueTime || null,
        companyId: form.companyId || null,
        contactId: form.contactId || null,
        leadId: form.leadId || null,
        dealId: form.dealId || null,
        assigneeId: form.assigneeId || null,
        participantIds: form.participantIds,
        participantOrganizationIds: form.participantOrganizationIds,
      };
      if (task) {
        await tasksApi.update(task.id, data);
      } else {
        await tasksApi.create(data);
      }
      onSave();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка сохранения');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{task ? 'Редактировать задачу' : 'Новая задача'}</h3>
          <button onClick={onClose} style={{ background: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Название *</label>
              <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <SearchableSelect
                  label="Исполнитель"
                  value={form.assigneeId}
                  onChange={val => setForm({ ...form, assigneeId: val })}
                  options={users.map((u: any) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
                  emptyLabel="— Выберите —"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Приоритет</label>
                <select className="form-select" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                  <option value="LOW">Низкий</option>
                  <option value="MEDIUM">Средний</option>
                  <option value="HIGH">Высокий</option>
                  <option value="URGENT">Срочно</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">Статус</label>
                <select className="form-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  <option value="PENDING">Ожидает</option>
                  <option value="IN_PROGRESS">В работе</option>
                  <option value="COMPLETED">Выполнена</option>
                  <option value="CANCELLED">Отменена</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Срок</label>
                <input className="form-input" type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Время</label>
              <input className="form-input" type="time" value={form.dueTime} onChange={e => setForm({ ...form, dueTime: e.target.value })} />
            </div>
            <div className="form-group">
              <SearchableSelect
                label="Связанная компания"
                value={form.companyId}
                onChange={val => setForm({ ...form, companyId: val })}
                options={companies.map((c: any) => ({ value: c.id, label: c.name }))}
                emptyLabel="— Не связана —"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <SearchableSelect
                  label="Связанный контакт"
                  value={form.contactId}
                  onChange={val => setForm({ ...form, contactId: val })}
                  options={contacts.map((c: any) => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))}
                  emptyLabel="— Не связан —"
                />
              </div>
              <div className="form-group">
                <SearchableSelect
                  label="Связанный лид"
                  value={form.leadId}
                  onChange={val => setForm({ ...form, leadId: val })}
                  options={leads.map((l: any) => ({ value: l.id, label: `${l.firstName || ''} ${l.lastName || ''}`.trim() || l.companyName || l.id }))}
                  emptyLabel="— Не связан —"
                />
              </div>
            </div>
            <div className="form-group">
              <SearchableSelect
                label="Связанная сделка"
                value={form.dealId}
                onChange={val => setForm({ ...form, dealId: val })}
                options={deals.map((d: any) => ({ value: d.id, label: d.title }))}
                emptyLabel="— Не связана —"
              />
            </div>
            <div className="form-group">
              <MultiSearchableSelect
                label="Участники"
                value={form.participantIds}
                onChange={val => setForm({ ...form, participantIds: val })}
                options={users.map((u: any) => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))}
              />
            </div>
            <div className="form-group">
              <MultiSearchableSelect
                label="Организации-участники"
                value={form.participantOrganizationIds}
                onChange={val => setForm({ ...form, participantOrganizationIds: val })}
                options={companies.map((c: any) => ({ value: c.id, label: c.name }))}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Описание</label>
              <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? 'Сохранение...' : 'Сохранить'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
